import { Button } from "primereact/button";
import { useContext, useState } from "react";
import { UserManagementDataContext } from "../../providers/user-management-provider";
import { Card } from "primereact/card";
import { FileUpload, FileUploadSelectEvent } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { Avatar } from "primereact/avatar";
import { UserDto } from "../../general/dtos";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { IAuth, IUser, useAuth } from "../../providers/auth-provider";

export function UserEditor() {
    const auth: IAuth = useAuth();
    const authenticatedUser: IUser = auth.getUser()!;
    const ctx = useContext(UserManagementDataContext);
    const { toastRef, saveUser, cancelEditing, selectedUser, updateUserField } = ctx!;
    const [fileUploadKey, setFileUploadKey] = useState(0);

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        updateUserField(name as keyof UserDto, value);
    };

    const handleBooleanFieldChange = (name: keyof UserDto, value: boolean) => {
        updateUserField(name, value);
    };
    
    const handleAvatarSelect = (event: FileUploadSelectEvent) => {
        const input = event.originalEvent?.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) return;
    
        const reset = () => {
          setFileUploadKey(prev => prev + 1); // force re-render
        };
    
        const validTypes = ['image/jpeg', 'image/png', 'image/bmp'];
        if (!validTypes.includes(file.type)) {
          toastRef.current?.show({ severity: 'error', summary: 'Invalid format', detail: 'Only JPG, PNG, BMP allowed' });
          reset();
          return;
        }
    
        if (file.size > 50 * 1024) {
          toastRef.current?.show({ severity: 'warn', summary: 'File too large', detail: 'Max size is 50KB' });
          reset();
          return;
        }
    
        const img = new Image();
        img.onload = () => {
          if (img.width > 200 || img.height > 200) {
            toastRef.current?.show({ severity: 'warn', summary: 'Image too large', detail: 'Max 200x200 pixels' });
            reset();
          } else {
            const reader = new FileReader();
            reader.onload = () => {
              updateUserField('picture', reader.result as string);
              reset();
            };
            reader.readAsDataURL(file);
          }
        };
        img.onerror = () => {
          toastRef.current?.show({ severity: 'error', summary: 'Image load failed', detail: 'Could not read image' });
          reset();
        };
        img.src = URL.createObjectURL(file);
    };

    return (
        <div className="course-editor flex flex-column h-full">
            {/* Main area */}
            <div className="flex-grow-1 flex flex-column overflow-hidden">
                <Card title="Edit user information" className="shadow-2">
                    <div className="flex flex-column align-items-center mb-4">
                        <Avatar image={selectedUser?.picture} size="xlarge" shape="circle" className="mb-3" />
                        <span className="text-sm text-color-secondary">Live avatar preview</span>
                    </div>
            
                    <div className="p-fluid">
                        <div className="formgrid grid">
                        <div className="field col-12 md:col-6">
                            <label htmlFor="firstName">First name*</label>
                            <InputText id="first_name" name="first_name" value={selectedUser?.first_name} onChange={handleFieldChange} maxLength={200} required />
                        </div>
            
                        <div className="field col-12 md:col-6">
                            <label htmlFor="lastName">Last name*</label>
                            <InputText id="last_name" name="last_name" value={selectedUser?.last_name} onChange={handleFieldChange} maxLength={200} required />
                        </div>
                        </div>
            
                        <div className="field">
                        <label htmlFor="picture">Upload avatar (JPG, PNG, BMP, ≤50KB, ≤200×200)</label>
                        <FileUpload
                            key={fileUploadKey}
                            mode="basic"
                            name="picture"
                            accept=".jpg,.jpeg,.png,.bmp"
                            maxFileSize={50 * 1024}
                            customUpload={false}
                            auto
                            chooseLabel="Upload avatar"
                            onSelect={handleAvatarSelect}
                        />
                        </div>

                        <div className="field">
                            Email: <b>{selectedUser?.email}</b><br />
                            ID: <b>{selectedUser?.sub}</b>
                        </div>

                        <div className="field col-12 md:col-6 flex align-items-center">
                            <Checkbox disabled={authenticatedUser.sub === selectedUser?.sub} title="Is locked out" id="locked" name="locked" 
                                checked={Boolean(selectedUser?.locked)}
                                onChange={(event: CheckboxChangeEvent) => handleBooleanFieldChange('locked', event.checked ?? false)}
                            />
                            <label htmlFor={`locked`} className="ml-2 m-0 p-0 text-sm">Is locked out</label>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Bottom bar */}
            <div className="admin-toolbar p-3 border-top-1 surface-border flex justify-content-end gap-2">
                <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={cancelEditing} />
                <Button label="Save" icon="pi pi-check" onClick={saveUser} />
            </div>
        </div>
    );
}